<template>
    <div v-if="dt" class="row m-0 p-3">

        <div class="col-3 mb-5">
            <div class="border rounded p-3">
                <h5>General</h5>

                <div class="custom-control custom-switch">
                    <input class="custom-control-input" type="checkbox"
                           :value="!dt.isHidden"
                           :checked="!dt.isHidden"
                           @change="toggleVisibility"
                           id="isHidden">
                    <label class="custom-control-label text-dark pt-1" for="isHidden">
                        Visible
                    </label>
                </div>
                <div class="custom-control custom-switch mt-2">
                    <input v-model="dt.isTopLevel" class="custom-control-input" type="checkbox" id="isTopLevel">
                    <label class="custom-control-label text-dark pt-1" for="isTopLevel">
                        Add to the top menu
                    </label>
                </div>
                <div class="custom-control custom-switch mt-2">
                    <input v-model="dt.hasDocument" class="custom-control-input" type="checkbox" id="hasDocument">
                    <label class="custom-control-label text-dark pt-1" for="hasDocument">
                        Has document
                    </label>
                </div>
            </div>

            <h6 class="d-block text-muted mt-4">
                Subtypes
                <span @click="addSubtype" class="text-primary-light clickable">
                    <i class="fal fa-plus"></i>
                </span>
            </h6>
            <DocSubtypesList :docType="dt"/>
        </div>

        <div class="col-6">
            <DtPermissionMembers class="border" style="max-height: 450px"/>
        </div>

        <div class="col-3">
            <h6 class="d-block text-muted">
                Statuses
                <span @click="addStatus" class="text-primary-light clickable">
                    <i class="fal fa-plus"></i>
                </span>
            </h6>

            <draggable :value="orderedStatuses" @input="onOrderChange" class="list-group">
                <div v-for="status in orderedStatuses"
                     :key="status.uuid"
                     @mouseover="hoveredStatusId = status.uuid"
                     @mouseleave="hoveredStatusId = null"
                     class="list-group-item pr-2">
                    <span v-if="hoveredStatusId === status.uuid" class="drag-icon">
                        <i class="fa fa-grip-vertical"></i>
                    </span>
                    <DocStatusRow
                        :status="status"
                        @edit="editStatus"
                        @delete="onStatusDelete"/>
                </div>
            </draggable>
        </div>
        <WriteDocStatus modal-name="write-doc-status"/>
        <DeleteStatusWithDocs modal-name="delete-status-with-docs"/>
    </div>
</template>

<script>
import {mapGetters, mapMutations} from 'vuex'
import Draggable from 'vuedraggable'
import DocStatusRow from './statuses/DocStatusRow'
import {docStatusGroups} from '@/models/documents/statuses'
import {
    DOCS_TYPES_SELECTED_UPDATE_PROP,
    DOCS_TYPES_SELECTED_UPDATE_STATUSES,
    DOCS_TYPES_SET
} from '@/store/modules/documents'
import DtPermissionMembers from '../permissions/DtPermissionMembers'
import DocSubtypesList from '../../subtypes/DocSubtypesList'
import {AppDocSystemStatuses} from '@/types/document/status'
import WriteDocStatus from './statuses/WriteDocStatus'
import DeleteStatusWithDocs from '@/views/documents/types/settings/statuses/DeleteStatusWithDocs'
import {documentTypesApiService} from '@/api/document/types'
import {showErrorMessage} from '@/services/snackbar'
import {getStatusDocsCount} from '@/views/documents/types/settings/statuses'
import {withConfirmation} from '@/decorators/withConfirmation'

export default {
    name: 'DtSettings',
    components: {
        Draggable,
        DeleteStatusWithDocs,
        WriteDocStatus,
        DocSubtypesList,
        DtPermissionMembers,
        DocStatusRow
    },
    data() {
        return {
            draggedStatus: null,
            removingUserUuid: null,
            updatedPermissionIdx: -1,
            isDragSaving: false,
            hoveredStatusId: null,

            docStatusGroups
        }
    },
    computed: {
        ...mapGetters([
            'getMemberByUuid'
        ]),

        ...mapGetters('documents', {
            documentTypes: 'documentTypes',
            dt: 'documentTypeSelected'
        }),

        dtStatuses() {
            return this.dt.statuses ? this.dt.statuses.filter(s => ![s, s.key].includes(AppDocSystemStatuses.FLAGGED_HIDDEN)) : []
        },

        orderedStatuses() {
            return _.sortBy(this.dtStatuses, 'order')
        }
    },
    methods: {
        ...mapMutations('documents', {
            setDocTypes: DOCS_TYPES_SET,
            updateDtProp: DOCS_TYPES_SELECTED_UPDATE_PROP,
            updateSelectedDocTypeStatuses: DOCS_TYPES_SELECTED_UPDATE_STATUSES
        }),

        addStatus() {
            this.$modal.show('write-doc-status', {
                operation: 'add',
                docTypeId: this.dt.uuid,
                afterSave: this.onStatusSave,
                reservedStatusesKeys: this.dtStatuses.filter(s => s.key).map(s => s.key)
            })
        },

        editStatus(status) {
            this.$modal.show('write-doc-status', {
                operation: 'edit',
                docTypeId: this.dt.uuid,
                status,
                afterSave: this.onStatusSave,
                reservedStatusesKeys: this.dtStatuses.filter(s => s.key && s.key !== status.key).map(s => s.key)
            })
        },

        onStatusSave(status) {
            if (!status || !status.uuid) {
                return
            }
            const idx = this.dt.statuses.findIndex(s => s.uuid === status.uuid)
            if (idx === -1) {
                const statuses = _.cloneDeep(this.dt.statuses)
                statuses.push(status)
                this.updateDtProp({path: ['statuses'], data: statuses})
            } else {
                this.updateDtProp({path: ['statuses', idx], data: status})
            }
        },

        async onStatusDelete(status) {
            try {
                const statusDocsCount = await getStatusDocsCount({docType: this.dt.type, status})
                if (statusDocsCount === 0) {
                    this.deleteStatusWithConfirmation(status)
                } else {
                    this.deleteStatusWithDocs(status, statusDocsCount)
                }
            } catch (e) {
                showErrorMessage()
            }
        },

        deleteStatusWithDocs(status, statusDocsCount) {
            this.$modal.show('delete-status-with-docs', {
                docType: this.dt.type,
                status,
                statusDocsCount,
                statusesAvailableForMovingDocsTo: this.dt.statuses.filter(s => s.key !== status.key && s.isEnabled),
                onDelete: () => this.deleteStatus(status)
            })
        },

        deleteStatusWithConfirmation(status) {
            withConfirmation({
                title: 'Delete status',
                description: `Are you sure you want to delete status "${status.name}"?`,
                onConfirm: () => this.deleteStatus(status)
            })
        },

        async deleteStatus(status) {
            await documentTypesApiService.deleteStatus(this.dt.uuid, status.uuid)
            this.deleteStatusFromDocTypeModel(status)
        },

        deleteStatusFromDocTypeModel(status) {
            const idx = this.dt.statuses.findIndex(s => s.uuid === status.uuid)
            const statuses = _.cloneDeep(this.dt.statuses)
            if (idx > -1) {
                statuses.splice(idx, 1)
            }
            this.updateDtProp({path: 'statuses', data: statuses})
        },

        addSubtype() {
            this.$modal.show('add-document-subtype')
        },

        toggleVisibility() {
            this.updateDtProp({path: 'isHidden', data: !this.dt.isHidden})
        },

        onOrderChange(statuses) {
            const orderedStatuses = statuses.filter(s => s.key).map((s, idx) => ({...s, order: idx}))
            const updateStatusesPayload = orderedStatuses.map(s => ({status: s.key, order: s.order}))
            documentTypesApiService.reorderStatuses(this.dt.uuid, updateStatusesPayload)
            this.updateSelectedDocTypeStatuses(orderedStatuses)
        }
    }
}
</script>

<style lang="scss" scoped>
.drag-icon {
    position: absolute;
    top: 13.5px;
    left: 8px;
    color: lightgrey;
}

.list-group-item {
    cursor: grab;
}
</style>
